import { decode, encode } from 'rison';
import { composeExternalStorageHook } from '@amtrav/external-storage-helpers';

const getHashSafeQueryString = () => {
  const parts = {
    params: undefined,
    hash: undefined
  };
  const [, qs] = window.location.href.split('?');
  if (!qs) {
    return {
      ...parts,
      hash: window.location.hash || undefined
    };
  }

  // Rison won't encode & and = chars so we have to split it all up manually
  const rawJsr = qs.match(/(jsr=\(.*\))/g);
  const uriParams = qs.replace(rawJsr, '').split('&');
  const JSR_IDENTIFIER = 'jsr=';
  const params = [...(rawJsr || []), ...uriParams].reduce((acc, param, i, srcArray) => {
    if (!param.length) {
      return acc;
    }
    let [key, value] = param.indexOf(JSR_IDENTIFIER) === 0 ? ['jsr', param.slice(JSR_IDENTIFIER.length)] : param.split('=');
    if (key === 'jsr') {
      value = decode(decodeURIComponent(value));
    }
    // check for a legit hash on the last key
    const isLastPair = i === srcArray.length - 1;
    if (isLastPair && typeof value === 'string') {
      const [realValue, realHash] = value.split('#');
      parts.hash = realHash ? `#${realHash}` : undefined;
      value = realValue;
    }
    if (isLastPair && typeof value === 'undefined') {
      const [realKey, realHash] = key.split('#');
      parts.hash = realHash ? `#${realHash}` : undefined;
      key = realKey;
    }
    acc[key] = value;
    return acc;
  }, {});
  return {
    ...parts,
    params
  };
};
const objectToQueryString = obj => {
  const {
    jsr,
    ...rest
  } = obj;
  const jsrString = jsr ? `jsr=${jsr}` : '';
  const remainingParams = Object.entries(rest).map(_ref => {
    let [key, value] = _ref;
    return `${key}=${value}`;
  }).join('&');
  return `${jsrString}${remainingParams.length ? `&${remainingParams}` : ''}`;
};
const encodeObjToJsrString = obj => {
  return encode(obj);
};
const getJsr = () => {
  const {
    params: {
      jsr
    }
  } = getHashSafeQueryString();
  if (!jsr) {
    throw new Error('jsr is not present in the URI.');
  }
  return jsr;
};
const getJsrOrDefault = function () {
  let defaultVal = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  try {
    return getJsr();
  } catch (error) {
    return defaultVal;
  }
};
const setJsr = function (newData) {
  let windowState = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const {
    params = {},
    hash = ''
  } = getHashSafeQueryString();
  const currentJsr = params.jsr || {};
  // get a new object and encodes any string values in the object.
  const newJsr = {
    ...currentJsr,
    ...newData
  };
  params.jsr = encode(newJsr);
  const newQueryString = objectToQueryString(params);
  window.history.replaceState(windowState || window.history.state, document.title, `?${newQueryString}${hash}`);
};
const pushJsrToHistoryStack = function (newData) {
  let windowState = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const {
    params = {},
    hash = ''
  } = getHashSafeQueryString();
  const currentJsr = params.jsr || {};
  // get a new object and encodes any string values in the object.
  const newJsr = {
    ...currentJsr,
    ...newData
  };
  params.jsr = encode(newJsr);
  const newQueryString = objectToQueryString(params);
  window.history.pushState(windowState || window.history.state, document.title, `?${newQueryString}${hash}`);
};
const removeFromJsr = function (keysArray) {
  let windowState = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const {
    params,
    hash = ''
  } = getHashSafeQueryString();
  params.jsr = params.jsr || {};
  keysArray.forEach(key => {
    delete params.jsr[key];
  });
  params.jsr = encode(params.jsr);
  window.history.replaceState(windowState || window.history.state, document.title, `?${objectToQueryString(params)}${hash}`);
};
const importParamsIntoJsr = function (keysArray) {
  let windowState = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const {
    params,
    hash = ''
  } = getHashSafeQueryString();
  params.jsr = params.jsr || {};
  keysArray.forEach(key => {
    if (!Object.keys(params).includes(key)) {
      throw new Error(`${key} does not exist in the query string`);
    }
    params.jsr[key] = params[key] || null;
    delete params[key];
  });
  params.jsr = encode(params.jsr);
  window.history.replaceState(windowState || window.history.state, document.title, `?${objectToQueryString(params)}${hash}`);
};

/**
 * Adapter for use with custom hooks provided by @amtrav/external-storage-helpers. This maps
 * a single key within JSR to a value that is bound as a representation of a react state variable.
 */
const jsrAdapter = {
  set: (key, val) => {
    // undefined values in JSR need to be removed
    if (val === undefined) {
      return;
    }
    const jsr = getJsrOrDefault();
    const newJsr = {
      ...jsr,
      [key]: val
    };
    setJsr(newJsr);
  },
  get: key => {
    const jsr = getJsrOrDefault();
    return jsr[key];
  },
  remove: key => removeFromJsr([key])
};

/**
 * Composes a storage hook for a jsr value using the jsrAdapter. This hook
 * will only work for one key inside the JSR object.
 *
 * @param {string} key
 */
const useJsrHook = key => composeExternalStorageHook(key, jsrAdapter)();

export { encodeObjToJsrString, getHashSafeQueryString, getJsr, getJsrOrDefault, importParamsIntoJsr, jsrAdapter, objectToQueryString, pushJsrToHistoryStack, removeFromJsr, setJsr, useJsrHook };
//# sourceMappingURL=index.esm.js.map
